import { useEffect, useState } from "react";
import { TrendingUp, Share2, AlertTriangle, Eye } from "lucide-react";
import { getRoutingSummary } from "../lib/api";

const Card = ({ Icon, title, children, testid, accent = "white" }) => (
    <div
        data-testid={testid}
        className="bg-[#0a0a0a] border border-[#262626] p-4 space-y-2"
    >
        <div className="d8-label inline-flex items-center gap-2">
            <Icon className={`w-3.5 h-3.5 ${accent === "red" ? "text-[#ff2a2a]" : "text-[#f5f5f0]"}`} strokeWidth={2} />
            {title}
        </div>
        {children}
    </div>
);

const PlatformLine = ({ label, value }) => (
    <div className="flex items-center justify-between">
        <span className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-wider">{label}</span>
        <span className="font-mono-d8 text-xs text-[#f5f5f0] tabular-nums">{value}</span>
    </div>
);

export const RoutingPanel = () => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        getRoutingSummary().then(setData).catch((e) => setErr(e?.message || "failed"));
    }, []);

    if (err) {
        return (
            <div data-testid="routing-panel-error" className="bg-[#0a0a0a] border border-[#262626] p-4 text-[#8a8a8a] font-mono-d8 text-xs">
                Routing summary unavailable.
            </div>
        );
    }
    if (!data) {
        return (
            <div data-testid="routing-panel-loading" className="bg-[#0a0a0a] border border-[#262626] p-4 text-[#8a8a8a] font-mono-d8 text-xs">
                Loading routing summary…
            </div>
        );
    }

    if (data.data_state === "empty") {
        return (
            <div data-testid="routing-panel-empty" className="bg-[#0a0a0a] border border-[#262626] p-4 space-y-1">
                <div className="d8-label">Routing summary</div>
                <p className="font-mono-d8 text-[11px] text-[#8a8a8a]">
                    No analytics events yet. Ingest performance data to see top channels and risk leaders.
                </p>
            </div>
        );
    }

    const top = data.top_completion_channel;
    const ss = data.top_share_save_platform;
    const risk = data.highest_risk_platform;
    const review = data.clips_needing_review || [];

    return (
        <div data-testid="routing-panel" className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            <Card Icon={TrendingUp} title="Top completion channel" testid="routing-top-channel">
                {top ? (
                    <>
                        <div className="font-heading font-bold text-[#f5f5f0] uppercase text-base">{top.channel_name}</div>
                        <PlatformLine label="Platform" value={top.platform} />
                        <PlatformLine label="Completion" value={`${Math.round((top.completion_rate_avg || 0) * 100)}%`} />
                        <PlatformLine label="Events" value={top.event_count} />
                    </>
                ) : (
                    <div className="font-mono-d8 text-[11px] text-[#8a8a8a]">No channel-tagged events yet.</div>
                )}
            </Card>

            <Card Icon={Share2} title="Top share/save platform" testid="routing-top-platform">
                {ss ? (
                    <>
                        <div className="font-heading font-bold text-[#f5f5f0] uppercase text-base">{ss.platform}</div>
                        <PlatformLine label="Share+save rate" value={`${(ss.share_save_rate * 100).toFixed(2)}%`} />
                        <PlatformLine label="Shares" value={`${(ss.share_rate * 100).toFixed(2)}%`} />
                        <PlatformLine label="Saves" value={`${(ss.save_rate * 100).toFixed(2)}%`} />
                    </>
                ) : (
                    <div className="font-mono-d8 text-[11px] text-[#8a8a8a]">—</div>
                )}
            </Card>

            <Card Icon={AlertTriangle} title="Highest-risk platform" testid="routing-risk-platform" accent="red">
                {risk ? (
                    <>
                        <div className="font-heading font-bold text-[#ff2a2a] uppercase text-base">{risk.platform}</div>
                        <PlatformLine label="Combined risk" value={`${(risk.risk_rate * 100).toFixed(3)}%`} />
                        <PlatformLine label="Reports" value={`${(risk.report_rate * 100).toFixed(3)}%`} />
                        <PlatformLine label="Negative fb" value={`${(risk.negative_rate * 100).toFixed(3)}%`} />
                    </>
                ) : (
                    <div className="font-mono-d8 text-[11px] text-[#8a8a8a]">—</div>
                )}
            </Card>

            <div data-testid="routing-review-queue" className="md:col-span-3 bg-[#0a0a0a] border-t border-[#262626] p-4 space-y-2">
                <div className="d8-label inline-flex items-center gap-2">
                    <Eye className="w-3.5 h-3.5 text-[#f5f5f0]" /> Clips needing routing review
                </div>
                {review.length === 0 ? (
                    <p className="font-mono-d8 text-[11px] text-[#8a8a8a]">Nothing flagged.</p>
                ) : (
                    <div className="grid gap-px bg-[#262626]">
                        {review.map((r) => (
                            <div key={r.clip_id} className="bg-[#050505] p-3 flex items-center justify-between gap-3">
                                <div>
                                    <div className="font-heading font-bold uppercase text-[#f5f5f0] text-sm">{r.title}</div>
                                    <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-wider">
                                        {(r.label || "n/a").replace(/_/g, " ")} · report {(r.report_rate * 100).toFixed(2)}%
                                    </div>
                                </div>
                                <div className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#ffb800]">
                                    {r.reasons.join(" · ")}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoutingPanel;
